// apps/client/src/state/slices/title.ts (title screen, 2026-10-05) — every launch opens on the title (ui/screens/title.tsx) over the
// turning showcase town (title/backdrop.ts). Play now starts the camera's swoop down into the town (or, with reduced motion, a short
// fade); when it ends the title is done for the rest of the launch, and App shows the phase's own screen (ui/router.ts baseScreen).
// Kept in memory only: a launch is a page load, so a reload or a fresh start of the home-screen app shows the title again.
// Portrait landing (2026-10-18): the title is the one screen that works upright. Play now on a phone held upright waits ('turn') for it
// to turn sideways, under the rotate card (ui/rotate.tsx), and turning it starts the swoop; everything after needs landscape.
import { tuning } from '@whiskerton/content';
import type { SliceCreator } from '../types';
import type { Phase } from './core';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- declaration merging into GameStore
declare module '../types' { interface GameStore extends TitleSlice {} }

/** 'title': the landing page. 'turn': Play now was pressed on a phone held upright; waiting for it to turn sideways. 'swoop': the
 *  camera comes down. 'done': the phase's own screens for the rest of the launch. */
export type TitleStep = 'title' | 'turn' | 'swoop' | 'done';
export type TitleMotion = 'swoop' | 'fade';
export interface TitlePlan { motion: TitleMotion; ms: number }
/** The camera swoops down into the town, or (prefers-reduced-motion) the view fades through the sky to the same place. */
export function titlePlan(reducedMotion: boolean): TitlePlan {
  return reducedMotion ? { motion: 'fade', ms: tuning.title.fadeMs } : { motion: 'swoop', ms: tuning.title.swoopMs };
}
/** Whether the title is on screen: until Play now has run its swoop, except over a town on its way in or on screen, and the update
 *  prompt its socket raises, which the title never covers (the title is always done by then in a real launch). */
export function titleShowing(step: TitleStep, phase: Phase): boolean {
  return step !== 'done' && phase !== 'connecting' && phase !== 'inTown' && phase !== 'updateRequired';
}
/** Whether what is on screen needs the phone sideways (a phone held upright shows the rotate card over it): everything except the
 *  landing page before Play now. */
export function needsLandscape(step: TitleStep, phase: Phase): boolean {
  return !(step === 'title' && titleShowing(step, phase));
}
export interface TitleSlice {
  titleStep: TitleStep;
  titleMotion: TitleMotion;
  /** performance.now() when Play now was pressed: the swoop's start, read by the backdrop's camera. Null before. */
  titleAt: number | null;
  /** Play now: the swoop (or fade) starts at `now` and the title is done `plan.ms` later. Only the first press counts (from the landing
   *  page, or from waiting for the phone to turn). */
  playNow(plan: TitlePlan, now: number): void;
  /** Play now on a phone held upright: wait for it to turn sideways (then the title calls playNow). Only from the landing page. */
  waitForLandscape(): void;
  /** The rotate card's Back while waiting: the landing page again. */
  titleBack(): void;
}

export const slice: SliceCreator<TitleSlice> = (set, get) => ({
  titleStep: 'title',
  titleMotion: 'swoop',
  titleAt: null,
  playNow(plan, now) {
    const step = get().titleStep;
    if (step !== 'title' && step !== 'turn') return;
    set({ titleStep: 'swoop', titleMotion: plan.motion, titleAt: now });
    setTimeout(() => set({ titleStep: 'done' }), plan.ms);
  },
  waitForLandscape() {
    if (get().titleStep === 'title') set({ titleStep: 'turn' });
  },
  titleBack() {
    if (get().titleStep === 'turn') set({ titleStep: 'title' });
  },
});
